import api from './api';

const warehouseService = {
  // Listings
  getListings: (params) => api.get('/listings', { params }),
  getListing: (id) => api.get(`/listings/${id}`),
  createListing: (data) => api.post('/listings', data),
  // Backend only exposes PATCH /listings/{id} (UpdateListingUseCase) —
  // there is no PUT route; using PUT 404'd every update attempt.
  updateListing: (id, data) => api.patch(`/listings/${id}`, data),
  deleteListing: (id) => api.delete(`/listings/${id}`),
  submitForReview: (id) => api.post(`/listings/${id}/submit`),
  publishListing: (id) => api.post(`/listings/${id}/publish`),
  unpublishListing: (id) => api.post(`/listings/${id}/unpublish`),

  // Host visibility toggle. PATCH /listings/{id}/status flips between
  // ACTIVE (shown in search + listings) and INACTIVE (hidden but
  // recoverable). Approved-state-only — the backend rejects this
  // path when the listing is DRAFT or REJECTED.
  setListingStatus: (id, status) => api.patch(`/listings/${id}/status`, { status }),

  // Admin approval
  approveListing: (id, notes) => api.post(`/listings/${id}/approve`, { notes }),
  rejectListing: (id, reason) => api.post(`/listings/${id}/reject`, { reason }),
  holdListing: (id, note) => api.post(`/listings/${id}/hold`, { note }),
  requestListingInfo: (id, note) => api.post(`/listings/${id}/request-info`, { note }),
  listPendingListings: (page = 1, page_size = 20) =>
    api.get('/listings/admin/pending', { params: { page, page_size } }),
  // Approvals tabs — tab ∈ pending|approved|rejected|on_hold|needs_info
  listListingsByStatus: (tab = 'pending', page = 1, page_size = 20) =>
    api.get('/listings/admin/by-status', { params: { tab, page, page_size } }),

  // Photos
  uploadPhotos: (id, formData) =>
    api.post(`/listings/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deletePhoto: (id, photoId) => api.delete(`/listings/${id}/photos/${photoId}`),

  // Floor plan (layout with camera/security markers — annotations editor TBD)
  uploadFloorPlan: (id, formData) =>
    api.post(`/listings/${id}/floor-plan`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteFloorPlan: (id) => api.delete(`/listings/${id}/floor-plan`),
  updateFloorPlanAnnotations: (id, annotations) =>
    api.put(`/listings/${id}/floor-plan/annotations`, { annotations }),

  // Search / marketplace
  searchListings: (params) => api.get('/search', { params }),
  getPublicListing: (id) => api.get(`/listings/${id}`),
  // Anonymous visitors: returns a privacy-safe subset (no owner name or contacts).
  getListingPublicMasked: (id) => api.get(`/listings/public/${id}`),

  // Contract preview (Phase 3b) — returns the template a tenant would
  // sign if they booked this listing, with listing-known fields
  // (rate, area, host) populated and tenant fields stubbed as
  // placeholders. Public endpoint, no auth required.
  getListingContractPreview: (id) => api.get(`/listings/${id}/contract-preview`),
  downloadListingContractPreviewPdf: (id) =>
    api.get(`/listings/${id}/contract-preview.pdf`, { responseType: 'blob' }),
  downloadListingContractPreviewDocx: (id) =>
    api.get(`/listings/${id}/contract-preview.docx`, { responseType: 'blob' }),

  // Tenant-scoped detail-page field spec (which fields are visible + ordering).
  getListingFieldSpec: () => api.get('/listing-fields/spec'),
  updateListingFieldSpec: (items) => api.put('/listing-fields/spec', { items }),

  // Availability (paths align with backend availability_router.py)
  getAvailability: (id, params) => api.get(`/availability/${id}`, { params }),
  // Phase B — per-day timeline. Each entry carries total/booked/available
  // sqft + a blackout flag so the calendar can render heat-mapped cells
  // and surface "first available date" hints.
  getAvailabilityTimeline: (id, params) =>
    api.get(`/availability/${id}/timeline`, { params }),
  updateBlackoutDates: (id, blackout_dates) =>
    api.put(`/availability/${id}/blackout`, { blackout_dates }),

  // Stats
  getListingStats: (id) => api.get(`/listings/${id}/stats`),
  getMyListingsStats: () => api.get('/listings/stats/summary'),

  // --- Phase A: warehouse↔listing mapping -----------------------------
  // Decoupled CRUD for the warehouse asset itself + capacity surface so
  // a host can carve one warehouse into multiple listings.
  createWarehouse: (data) => api.post('/warehouses', data),
  getMyWarehouses: () => api.get('/warehouses'),
  getWarehouseCapacity: (id) => api.get(`/warehouses/${id}/capacity`),
  createListingUnderWarehouse: (warehouseId, data) =>
    api.post(`/warehouses/${warehouseId}/listings`, data),
};

export default warehouseService;
